/**
 * Calendar EXTREME — Virtualization Contract
 *
 * These rules prevent iOS Safari/WKWebView ghost DOM, remount storms, and
 * duplicate hour/grid paint. Violations must fail loudly in development.
 *
 * Pool objects do NOT remount on content change.
 * Pool keys are permanent for the life of the viewport instance.
 * Content refresh updates props (contentRefreshToken), never the React key.
 * Scroll / zoom / resize / search / selection / drag / notes / blackouts /
 * month data updates must NOT remount the viewport or the pool.
 *
 * Remounting UltraStructureGrid or UltraGridViewport is allowed only when the
 * calendar route itself unmounts (leave page), not on scroll-host rebind.
 */

export const ULTRA_POOL_KEY_PREFIX = "pool-";

/** Max viewport mounts expected during a single page session (StrictMode = 2). */
export const ULTRA_DEV_VIEWPORT_MOUNT_BUDGET = 4;

/** Max pool-slot remounts for the same poolIndex while one viewport is alive. */
export const ULTRA_DEV_POOL_SLOT_REMOUNT_BUDGET = 1;

export function buildStablePoolSlotKey(poolIndex) {
   const index = Math.max(0, Math.trunc(Number(poolIndex) || 0));
   return `${ULTRA_POOL_KEY_PREFIX}${index}`;
}

/**
 * Reject keys that bake tokens/versions into the identity (the iOS ghost bug).
 * @returns {{ ok: boolean, reason?: string }}
 */
export function validatePoolSlotKey(key, poolIndex) {
   const expected = buildStablePoolSlotKey(poolIndex);
   const actual = String(key ?? "");
   if (actual !== expected) {
      return {
         ok: false,
         reason: `unstable pool key "${actual}" (expected "${expected}")`,
      };
   }
   if (/contentRefresh|token|version|revision|epoch|ts|timestamp/i.test(actual)) {
      return {
         ok: false,
         reason: `pool key must not embed refresh/version tokens: "${actual}"`,
      };
   }
   return { ok: true };
}

export function isUltraVirtualizationDevMode() {
   if (typeof process !== "undefined" && process.env?.NODE_ENV === "production") {
      return false;
   }
   return true;
}
